import { faQuestionCircle } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@gymflow/helpers";
import {
  FormInput,
  FormLabeledSwitch,
  FormMembersPriceBlock,
  FormPriceBlock,
  FormRadioButtonGroup,
} from "apps/portal/src/components/atoms";
import CustomTooltip from "apps/portal/src/components/atoms/CustomTooltip";
import { useIsLargeScreen } from "apps/portal/src/hooks";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import {
  AppointableFormValues,
  PricingStructure,
  TypeOfCost,
} from "../../types";
import { PRICING_STRUCTURE_OPTIONS, TYPE_OF_COST_OPTIONS } from "./constants";

export const AppointableWizardPricingStructureForm = () => {
  const { t } = useTranslation();
  const { values } = useFormikContext<AppointableFormValues>();
  const isLargeScreen = useIsLargeScreen();

  return (
    <div className="flex flex-col gap-6">
      <div className="text-lg font-semibold text-gray-950">
        {t(
          "page.settings.tab.appointments.appointableWizard.appointableWizardPricingStructureForm.ricingStructure",
        )}
      </div>
      <div className="flex flex-col gap-4 lg:h-10 lg:max-h-10 lg:min-h-10 lg:flex-row lg:items-center lg:justify-between">
        <FormRadioButtonGroup<AppointableFormValues, PricingStructure>
          name="pricingStructure.pricingStructure"
          className="gap-12"
          options={PRICING_STRUCTURE_OPTIONS}
        />
        {values.pricingStructure.pricingStructure === "FIXED_PRICE" && (
          <div className="shadow-xs flex h-10 max-h-10 min-h-10 w-full flex-row items-center justify-between rounded-[0.625rem] border border-gray-200 px-3 lg:max-w-[20.3125rem]">
            <div className="flex flex-row items-center gap-0.5">
              <div className="text-sm font-medium text-gray-950">
                {t(
                  "page.settings.tab.appointments.appointableWizard.appointableWizardPricingStructureForm.isUpfrontPaymentRequired.label",
                )}
              </div>
              <CustomTooltip
                message={t(
                  "page.settings.tab.appointments.appointableWizard.appointableWizardPricingStructureForm.isUpfrontPaymentRequired.helpTooltip",
                )}
              >
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="size-4 text-gray-500"
                />
              </CustomTooltip>
            </div>
            <FormLabeledSwitch<AppointableFormValues> name="pricingStructure.isUpfrontPaymentRequired" />
          </div>
        )}
      </div>
      {values.pricingStructure.pricingStructure === "FIXED_PRICE" && (
        <>
          <div className="h-px w-full bg-gray-300" />
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-4 lg:h-10 lg:max-h-10 lg:min-h-10 lg:flex-row lg:items-center lg:justify-between">
              <FormRadioButtonGroup<AppointableFormValues, TypeOfCost>
                name="pricingStructure.typeOfCost"
                verticalLayout={!isLargeScreen}
                className="gap-4 lg:gap-12"
                options={TYPE_OF_COST_OPTIONS}
              />
              {["PRICE", "BOTH"].includes(
                values.pricingStructure.typeOfCost,
              ) && (
                <div className="shadow-xs flex h-10 max-h-10 min-h-10 w-full flex-row items-center justify-between rounded-[0.625rem] border border-gray-200 px-3 lg:max-w-[20.3125rem]">
                  <div className="flex flex-row items-center gap-0.5">
                    <div className="text-sm font-medium text-gray-950">
                      {t(
                        "page.settings.tab.appointments.appointableWizard.appointableWizardPricingStructureForm.isShowMembersPrice.label",
                      )}
                    </div>
                    <CustomTooltip
                      message={t(
                        "page.settings.tab.appointments.appointableWizard.appointableWizardPricingStructureForm.isShowMembersPrice.helpTooltip",
                      )}
                    >
                      <FontAwesomeIcon
                        icon={faQuestionCircle}
                        className="size-4 text-gray-500"
                      />
                    </CustomTooltip>
                  </div>
                  <FormLabeledSwitch<AppointableFormValues> name="pricingStructure.isShowMembersPrice" />
                </div>
              )}
            </div>
            <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
              <div className="flex flex-1 flex-col gap-4">
                {["CREDITS", "BOTH"].includes(
                  values.pricingStructure.typeOfCost,
                ) && (
                  <FormInput<AppointableFormValues>
                    name="pricingStructure.credits"
                    type="number"
                    endIcon={
                      <div className="text-base font-normal text-gray-500">
                        {t(
                          "page.settings.tab.appointments.appointableWizard.appointableWizardPricingStructureForm.credits.endText",
                        )}
                      </div>
                    }
                  />
                )}
                {["PRICE", "BOTH"].includes(
                  values.pricingStructure.typeOfCost,
                ) && (
                  <FormPriceBlock<AppointableFormValues> name="pricingStructure.price" />
                )}
              </div>
              <div
                className={cn("flex flex-1 flex-col", {
                  "lg:pt-14":
                    values.pricingStructure.typeOfCost === "BOTH" &&
                    values.pricingStructure.isShowMembersPrice,
                })}
              >
                {["PRICE", "BOTH"].includes(
                  values.pricingStructure.typeOfCost,
                ) &&
                  values.pricingStructure.isShowMembersPrice && (
                    <FormMembersPriceBlock<AppointableFormValues>
                      name="pricingStructure.membersPrice"
                      taxRate={values.pricingStructure.price.salesTaxRate || 0}
                    />
                  )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
